import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import axios from 'axios';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, 
  Search, 
  RefreshCcw, 
  Activity, 
  AlertCircle, 
  CheckCircle2, 
  FileText, 
  Download,
  Filter,
  Zap,
  Clock,
  Layers,
  DollarSign
} from 'lucide-react';
import { useRole } from '../hooks/useRole';
import { canProcessCorporateActions } from '../utils/roleUtils';
import { cn } from '../lib/utils';

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string }> = 
  ({ label, value, icon, color }) => (
    <motion.div 
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
          <div className="text-3xl font-bold text-white tracking-tight">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
          {icon}
        </div>
      </div>
    </motion.div>
  );

// ============== Interfaces ==============
interface CorporateAction {
  id: number;
  actionType: string;
  status: string;
  securityId: number;
  cusip: string;
  securityName: string;
  issuerName: string;
  announcementDate: string;
  recordDate: string;
  exDate: string;
  paymentDate: string;
  affectedPositions?: number;
  totalEntitlement?: number;
}

const CorporateActionsScreen: React.FC = () => {
  const { userRole } = useRole();
  const canProcess = canProcessCorporateActions(userRole);
  const [actions, setActions] = useState<CorporateAction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'ex-date' | 'processed'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const fetchActions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/corporate-actions');
      const data = response.data?.content || response.data || [];
      setActions(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Error fetching corporate actions:', err);
      if (err.response?.status === 429) {
        setError('Rate limit exceeded. Please wait a moment before trying again.');
      } else {
        setError('Failed to load corporate actions.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  const filteredActions = useMemo(() => {
    let result = actions;
    if (activeTab === 'pending') result = result.filter(a => a.status === 'PENDING' || a.status === 'ANNOUNCED');
    if (activeTab === 'ex-date') result = result.filter(a => a.status === 'EX_DATE');
    if (activeTab === 'processed') result = result.filter(a => a.status === 'PROCESSED');
    
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(a => 
        a.securityName?.toLowerCase().includes(s) || 
        a.cusip?.toLowerCase().includes(s) ||
        a.actionType?.toLowerCase().includes(s)
      );
    }
    return result;
  }, [actions, activeTab, searchTerm]);

  const columnDefs = useMemo<ColDef[]>(() => [
    { field: 'id', headerName: 'ID', width: 80, pinned: 'left', cellStyle: { fontWeight: 'bold', color: '#60a5fa' } as any },
    { 
      field: 'actionType', 
      headerName: 'Type', 
      flex: 1.5,
      cellRenderer: (p: any) => (
        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-indigo-500/10 text-indigo-400">
          {p.value?.replace('_', ' ')}
        </span>
      )
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 120,
      cellRenderer: (p: any) => {
        const processed = p.value === 'PROCESSED';
        const pending = p.value === 'PENDING' || p.value === 'ANNOUNCED';
        return (
          <span className={cn(
            "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider",
            processed ? "bg-emerald-500/10 text-emerald-400" : 
            pending ? "bg-amber-500/10 text-amber-400" : 
            "bg-slate-800 text-slate-400"
          )}>
            {p.value}
          </span>
        );
      }
    },
    { field: 'securityName', headerName: 'Security', flex: 2, cellStyle: { fontWeight: '600' } as any },
    { field: 'cusip', headerName: 'CUSIP', width: 120, cellStyle: { fontFamily: 'monospace', color: '#94a3b8' } as any },
    { field: 'exDate', headerName: 'Ex-Date', width: 120 },
    { field: 'paymentDate', headerName: 'Payment Date', width: 120 },
    { 
      field: 'totalEntitlement', 
      headerName: 'Entitlement', 
      width: 140,
      valueFormatter: (p) => p.value ? `$${p.value.toLocaleString()}` : '-',
      cellStyle: { textAlign: 'right', fontWeight: 'bold' } as any
    }
  ], []);

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <Calendar className="w-8 h-8 text-blue-500" />
                Corporate Actions
              </h1>
              <p className="text-slate-500 font-medium mt-1">Lifecycle management for dividends, splits, coupons, and mandatory events</p>
            </div>
            <div className="flex gap-3">
              <div className="flex bg-slate-900 border border-white/10 p-1 rounded-xl">
                {['all', 'pending', 'ex-date', 'processed'].map((tab) => (
                  <button 
                    key={tab}
                    onClick={() => setActiveTab(tab as any)}
                    className={cn(
                      "px-4 py-1.5 rounded-lg text-xs font-bold transition-all capitalize", 
                      activeTab === tab ? "bg-blue-600 text-white shadow-lg" : "text-slate-500 hover:text-slate-300"
                    )}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <button 
                onClick={fetchActions}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
              >
                <RefreshCcw className={cn("w-4 h-4", isLoading && "animate-spin")} />
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center justify-between gap-3 text-red-400 font-bold"
            >
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
              {error.includes('Rate limit') && (
                <button 
                  onClick={fetchActions}
                  className="px-3 py-1 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600 transition-colors"
                >
                  Retry Now
                </button>
              )}
            </motion.div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard label="Active Events" value={String(actions.length)} icon={<Calendar className="w-5 h-5" />} color="#3b82f6" />
            <StatCard label="Pending Processing" value={String(actions.filter(a => a.status === 'PENDING').length)} icon={<Clock className="w-5 h-5" />} color="#f59e0b" />
            <StatCard label="Ex-Date Today" value={String(actions.filter(a => a.status === 'EX_DATE').length)} icon={<Zap className="w-5 h-5" />} color="#10b981" />
            <StatCard label="Total Entitlement" value="$4.2M" icon={<DollarSign className="w-5 h-5" />} color="#8b5cf6" />
          </div>
        </motion.div>

        <GlassCard className="flex flex-col h-[calc(100vh-400px)] min-h-[500px]">
          <div className="p-4 border-b border-white/5 flex items-center justify-between bg-slate-900/30">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                <input 
                  type="text" 
                  placeholder="CUSIP, security or event type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 bg-slate-950/50 border border-white/10 rounded-xl text-sm text-white placeholder:text-slate-700 focus:outline-none focus:border-blue-500/50 transition-all w-80"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button className="p-2 text-slate-500 hover:text-white transition-colors"><Download className="w-4 h-4" /></button>
              <button className="p-2 text-slate-500 hover:text-white transition-colors"><Filter className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="flex-1 relative ag-theme-alpine-dark">
            <AgGridReact
              rowData={filteredActions}
              columnDefs={columnDefs}
              animateRows={true}
              pagination={true}
              paginationPageSize={25}
              headerHeight={48}
              rowHeight={44}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              onGridReady={(p) => p.api.sizeColumnsToFit()}
              getRowStyle={(params: any) => ({
                backgroundColor: params.node.rowIndex % 2 === 0 ? 'rgba(15, 23, 42, 0.3)' : 'transparent',
                borderBottom: '1px solid rgba(255, 255, 255, 0.03)'
              })}
            />
          </div>
        </GlassCard>
      </div>
    </Container>
  );
};

export default CorporateActionsScreen;
